import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ExtractionQuestion,
  QuestionType,
} from '../../../extraction-schema/domain/extraction-question.model';
import { AnswerRow, SKIPPED_VALUE } from '../../../extraction-answers/domain/types';
import { AnnotationRow } from '../../../claim-annotator/domain/types';
import { colorForClaim } from '../../../claim-annotator/domain/color-palette';

@Component({
  selector: 'app-data-extraction-questionnaire',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  templateUrl: './data-extraction-questionnaire.component.html',
})
export class DataExtractionQuestionnaireComponent implements OnChanges {
  @Input() questions: ExtractionQuestion[] = [];
  @Input() answers: AnswerRow[] = [];
  @Input() annotatedQuestionIds = new Set<number>();
  /**
   * Full annotation rows for the active document, used to display per-annotation
   * OCR extracted text under each question. Filtered internally by claim_id.
   */
  @Input() annotations: AnnotationRow[] = [];
  @Input() readOnly = false;
  @Input() saving = false;
  /**
   * When true, only the input control renders (no color swatch, question text or badges). Used by
   * the PI conflict-detail page, which already shows each question's text once above the answers.
   */
  @Input() hideHeader = false;
  /**
   * Scope key for per-question in-memory state (the pre-skip value stash). Hosts that reuse one
   * questionnaire instance across papers (the researcher workspace) must bind this to the paper
   * key, or unchecking a skip could restore a value typed on a different paper.
   */
  @Input() resetKey: unknown = null;

  @Output() answerChange = new EventEmitter<{ questionId: number; value: string }>();

  /**
   * Emitted when the user clicks an OCR text line — used to scroll the PDF viewer
   * to the corresponding page and highlight the annotation box.
   */
  @Output() ocrTextClick = new EventEmitter<{ annotationId: string; page: number }>();

  /**
   * Emitted when the user saves an edited OCR text line.
   */
  @Output() ocrTextSave = new EventEmitter<{ annotationId: string; text: string }>();

  /** Tracks which annotation's OCR text is currently being edited inline, or null. */
  editingOcrId: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resetKey'] && !changes['resetKey'].firstChange) {
      this.preSkipValues.clear();
    }
  }

  readonly QuestionType = QuestionType;

  // Per-instance id so radio groups in different component instances (e.g. the two
  // reviewer columns on the conflict-detail page) do not collide on the same DOM name.
  private static seq = 0;
  readonly instanceId = `dq${++DataExtractionQuestionnaireComponent.seq}`;

  // Pre-skip values stashed per question so a mis-clicked skip cannot destroy a typed
  // answer; restored on uncheck. In-memory only, scoped to this component instance.
  private readonly preSkipValues = new Map<number, string>();

  swatchColor(questionId: number): string {
    return colorForClaim(`q:${questionId}`);
  }

  isSkipped(questionId: number): boolean {
    return this.getAnswer(questionId) === SKIPPED_VALUE;
  }

  onSkipToggle(questionId: number, checked: boolean): void {
    if (checked) {
      const current = this.getAnswer(questionId);
      if (current && current !== SKIPPED_VALUE) {
        this.preSkipValues.set(questionId, current);
      }
      this.answerChange.emit({ questionId, value: SKIPPED_VALUE });
      return;
    }
    const restored = this.preSkipValues.get(questionId) ?? '';
    this.preSkipValues.delete(questionId);
    this.answerChange.emit({ questionId, value: restored });
  }

  typeLabel(type: QuestionType): string {
    switch (type) {
      case QuestionType.FULL_TEXT:
        return 'Text';
      case QuestionType.NUMERIC:
        return 'Number';
      case QuestionType.BOOLEAN:
        return 'Yes / No';
      case QuestionType.SINGLE_CHOICE:
        return 'Single choice';
      case QuestionType.MULTI_CHOICE:
        return 'Multiple choice';
      default:
        return type;
    }
  }

  annotationsFor(questionId: number): AnnotationRow[] {
    const claimId = `q:${questionId}`;
    return this.annotations.filter((a) => a.claim_id === claimId);
  }

  copyOcrText(text: string): void {
    void navigator.clipboard.writeText(text);
  }

  startEditOcr(annotationId: string): void {
    this.editingOcrId = annotationId;
  }

  saveEditedOcr(annotationId: string, newText: string): void {
    this.ocrTextSave.emit({ annotationId, text: newText });
    this.editingOcrId = null;
  }

  cancelEditOcr(): void {
    this.editingOcrId = null;
  }

  getAnswer(questionId: number): string {
    return this.answers.find((r) => r.questionId === questionId)?.value ?? '';
  }

  isChecked(questionId: number, optionId: string): boolean {
    const val = this.getAnswer(questionId);
    if (!val) return false;
    try {
      const arr: unknown[] = JSON.parse(val);
      return Array.isArray(arr) && arr.some((v) => String(v) === optionId);
    } catch {
      return false;
    }
  }

  onBooleanChange(questionId: number, value: string): void {
    this.answerChange.emit({ questionId, value });
  }

  onSingleChoiceChange(questionId: number, value: string): void {
    this.answerChange.emit({ questionId, value });
  }

  onMultiChoiceChange(questionId: number, optionId: string, checked: boolean): void {
    const val = this.getAnswer(questionId);
    let arr: string[] = [];
    if (val) {
      try {
        const parsed: unknown[] = JSON.parse(val);
        arr = Array.isArray(parsed) ? parsed.map(String) : [];
      } catch {
        arr = [];
      }
    }
    if (checked) {
      if (!arr.includes(optionId)) arr = [...arr, optionId];
    } else {
      arr = arr.filter((id) => id !== optionId);
    }
    this.answerChange.emit({ questionId, value: JSON.stringify(arr) });
  }

  onTextChange(questionId: number, value: string): void {
    this.answerChange.emit({ questionId, value });
  }

  onNumericChange(questionId: number, value: string): void {
    this.answerChange.emit({ questionId, value });
  }
}
